import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Eye } from "lucide-react";
import { impersonationApi } from "@/api/impersonation";
import { extractErrorMessage } from "@/api/client";
import { useAuth } from "@/hooks/useAuth";

// Admin User Impersonation — the staff-side "you are not yourself right now"
// banner. Mounted once in AppShell above the TopBar, so it is present on every
// staff route for as long as the impersonated session lasts.
//
// Deliberately loud: an admin who forgets they are impersonating will take
// real actions attributed to someone else, so a subtle banner would defeat the
// point of having one. Colour comes from the tokens (status-warning / onyx /
// gold) — no literal hex or px anywhere.

/**
 * Formats a remaining duration as `h:mm:ss` (or `m:ss` under an hour).
 * Exported so the countdown's shape can be pinned directly rather than
 * inferred from rendered text.
 */
export function formatRemaining(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return "expired";
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return hours > 0
    ? `${hours}:${pad(minutes)}:${pad(seconds)}`
    : `${minutes}:${pad(seconds)}`;
}

/** Live countdown to `expiresAt`; null when the session is not time-boxed. */
function useCountdown(expiresAt: string | null | undefined): string | null {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!expiresAt) return;
    const handle = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(handle);
  }, [expiresAt]);

  if (!expiresAt) return null;
  const target = Date.parse(expiresAt);
  if (Number.isNaN(target)) return null;
  return formatRemaining(target - now);
}

export function ImpersonationBanner() {
  const { user } = useAuth();
  const impersonation = user?.impersonation ?? null;
  const countdown = useCountdown(impersonation?.expiresAt);
  const [error, setError] = useState<string | null>(null);

  const exit = useMutation({
    mutationFn: () => impersonationApi.exitStaff(),
    // no-invalidation: the session identity changes underneath every cached
    // query, so this ends in a FULL page reload that discards the whole cache.
    // Invalidating instead would refetch as the impersonated user and leave
    // their data on screen under the admin's own identity.
    onSuccess: () => window.location.assign("/"),
    onError: (err: unknown) => setError(extractErrorMessage(err)),
  });

  if (!impersonation) return null;

  return (
    <div
      role="status"
      data-testid="impersonation-banner"
      className="w-full bg-status-warning text-onyx border-b-2 border-gold-shadow px-space-4 py-space-3"
    >
      <div className="flex flex-wrap items-center gap-space-3">
        <span className="inline-flex items-center gap-space-2 font-display font-bold uppercase tracking-label">
          <Eye size={16} strokeWidth={2} aria-hidden />
          <span className="text-label">Impersonating</span>
        </span>

        <span className="font-semibold">
          <span className="text-small">{impersonation.targetLabel}</span>
        </span>

        <span className="text-onyx/80">
          <span className="text-caption">
            Signed in as {impersonation.impersonatorDisplayName}
            {countdown ? ` · ${countdown} remaining` : null}
          </span>
        </span>

        <button
          type="button"
          onClick={() => {
            setError(null);
            exit.mutate();
          }}
          disabled={exit.isPending}
          className={
            "ml-auto inline-flex items-center rounded-md bg-onyx px-space-3 py-space-2 " +
            "font-display font-bold uppercase tracking-button text-gold " +
            "hover:bg-onyx/90 focus-visible:outline-none focus-visible:shadow-goldRing " +
            "disabled:opacity-40 disabled:cursor-not-allowed"
          }
        >
          <span className="text-label">
            {exit.isPending ? "Exiting…" : "Exit impersonation"}
          </span>
        </button>
      </div>

      {error && (
        <p role="alert" className="mt-space-2 mb-0 text-onyx">
          <span className="text-caption">{error}</span>
        </p>
      )}
    </div>
  );
}
